import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { authApi } from '@/services/api/endpoints/auth';
import { useAuth } from './useAuth';

/*
 * Mirrors the server's StrongPassword constraint. Duplicated deliberately: the
 * client copy exists to give immediate feedback, the server copy is the one that
 * decides. If they ever disagree, the server wins and the message surfaces on
 * the field through the API error handler.
 */
const schema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(10, 'Must be at least 10 characters')
      .regex(/[A-Z]/, 'Must include an upper-case letter')
      .regex(/[a-z]/, 'Must include a lower-case letter')
      .regex(/[0-9]/, 'Must include a digit'),
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type PasswordForm = z.infer<typeof schema>;

/**
 * Prompts a user whose account was created with an administrator-chosen
 * password. Shown as a dismissible banner rather than a blocking gate: the
 * account is still usable, and forcing a modal before any work can begin is
 * hostile when the reminder is not urgent.
 */
export function PasswordChangeBanner() {
  const { user, refreshProfile } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordForm>({ resolver: zodResolver(schema) });

  if (user === null || !user.mustChangePassword || dismissed) {
    return null;
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await authApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success('Password changed. Other sessions have been signed out.');
      setOpen(false);
      reset();
      await refreshProfile();
    } catch (error) {
      handleApiError(error, setError);
    }
  });

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 lg:px-6">
        <p className="text-sm text-amber-900">
          Your password was set by an administrator. Please choose your own.
        </p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setOpen(true)}>
            Change password
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDismissed(true)}>
            Later
          </Button>
        </div>
      </div>

      <Modal
        open={open}
        title="Change your password"
        description="All of your other sessions will be signed out."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSubmit} loading={isSubmitting}>
              Change password
            </Button>
          </>
        }
      >
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <TextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            required
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            hint="At least 10 characters, with upper case, lower case and a digit"
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            required
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </form>
      </Modal>
    </>
  );
}
